// ============================================================================
// USE MODULE COLOR HOOK
// Fetches the module_color from the modules table for a given module key.
// Returns the hex color defined in the database, falling back to a default.
// Date: February 2026
// ============================================================================

'use client';

import { useState, useEffect } from 'react';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';
import { authService } from '@/lib/authService';

// Cache module colors in memory to avoid redundant fetches
const moduleColorCache: Record<string, string> = {};

function normalizeModuleColorKey(moduleKey: string) {
  const normalizedKey = moduleKey.trim();
  if (normalizedKey === 'operations') {
    return 'total-operations';
  }

  return normalizedKey;
}

/**
 * Hook to retrieve the module_color from the database for a given module key.
 * 
 * @param moduleKey - The module key (e.g., 'total-operations', 'total-connect')
 * @param fallback - Fallback color if module_color is not set (default: '#3b82f6')
 * @returns { moduleColor, loading }
 */
export function useModuleColor(moduleKey: string, fallback = '#3b82f6') {
  const normalizedModuleKey = normalizeModuleColorKey(moduleKey);
  const [moduleColor, setModuleColor] = useState<string>(
    moduleColorCache[normalizedModuleKey] || fallback
  );
  const [loading, setLoading] = useState(Boolean(normalizedModuleKey) && !moduleColorCache[normalizedModuleKey]);

  useEffect(() => {
    if (!normalizedModuleKey) {
      setLoading(false);
      return;
    }

    // Skip fetch for known non-module keys (e.g. 'root' admin panel)
    const SKIP_KEYS = ['root', 'admin', 'settings', 'admin-hub'];
    if (SKIP_KEYS.includes(normalizedModuleKey)) {
      setLoading(false);
      return;
    }

    // Use cache if available
    if (moduleColorCache[normalizedModuleKey]) {
      setModuleColor(moduleColorCache[normalizedModuleKey]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchModuleColor() {
      try {
        // Use the public modules endpoint (no auth required)
        const response = await fetch(`/api/v1/modules/${normalizedModuleKey}`);
        if (!response.ok) return;

        const result = await response.json();
        if (result.success && result.data?.module_color) {
          const color = result.data.module_color;
          moduleColorCache[normalizedModuleKey] = color;
          if (!cancelled) {
            setModuleColor(color);
          }
        }
      } catch (err) {
        // Silently fail - use fallback color
        if (!isSilentFetchError(err)) console.warn(`[useModuleColor] Failed to fetch color for ${normalizedModuleKey}:`, err);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchModuleColor();

    return () => {
      cancelled = true;
    };
  }, [normalizedModuleKey, fallback]);

  return { moduleColor, loading };
}

export default useModuleColor;
